import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiX, FiPlus, FiStar, FiGlobe, FiEdit3, FiTrash2,
  FiSearch, FiGrid, FiCheck, FiFilePlus, FiBarChart2
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import { dashboardAPI } from '../../services/realApi';
import { getTemplatesForContext } from './widgetRegistry';
import { usePermissions } from '../../hooks/usePermissions';
import { useModuleAccess } from '../../hooks/useModuleAccess';

interface SystemView {
  name: string;
  description?: string;
  widgets: unknown[];
  isDefault?: boolean;
}

interface CustomDashboard {
  id: string | number;
  name: string;
  description?: string;
  context?: string;
  widget_count?: number;
  is_published?: boolean;
  is_default?: boolean;
  created_by?: string | number;
  created_by_name?: string;
  layout?: unknown;
}

interface DefaultPreference {
  default_type: string;
  default_key: string;
}

export interface SelectedView {
  type: 'system' | 'custom';
  view: SystemView | CustomDashboard;
  key: string | number;
}

interface Props {
  context?: string;
  onClose: () => void;
  onSelectView?: (view: SelectedView) => void;
  onDefaultChanged?: () => void;
}

const ViewsScreen: React.FC<Props> = ({
  context = 'hub',
  onClose,
  onSelectView,
  onDefaultChanged
}) => {
  const { user } = useAuth();
  const { selectedClientId } = useClient();
  const { isAdmin, hasPermission } = usePermissions();
  const { isAWSUser } = useModuleAccess();
  const navigate = useNavigate();

  const [customDashboards, setCustomDashboards] = useState<CustomDashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContext, setSelectedContext] = useState(context || 'all');
  const [currentDefault, setCurrentDefault] = useState<DefaultPreference | null>(null);

  const systemViews = getTemplatesForContext(context, user) as Record<string, SystemView>;
  const canSetDefault = true;

  // Suppress unused warnings
  void isAdmin;
  void hasPermission;
  void isAWSUser;

  const loadDefaultPreference = useCallback(async () => {
    if (!selectedClientId || !context) return;
    try {
      const result = await dashboardAPI.getDefaultDashboard(selectedClientId, context);
      if (result.success && result.data) {
        setCurrentDefault({
          default_type: result.data.default_type,
          default_key: result.data.default_key,
        });
      } else {
        setCurrentDefault(null);
      }
    } catch {
      setCurrentDefault(null);
    }
  }, [selectedClientId, context]);

  const loadCustomViews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await dashboardAPI.getDashboards(selectedClientId, true);
      if (result.success) {
        setCustomDashboards(result.data || []);
      } else {
        setError(result.error || 'Failed to load views');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  useEffect(() => {
    loadCustomViews();
    loadDefaultPreference();
  }, [loadCustomViews, loadDefaultPreference]);

  const isSystemDefault = (templateKey: string): boolean => {
    if (currentDefault) {
      return currentDefault.default_type === 'system' && currentDefault.default_key === templateKey;
    }
    const view = systemViews[templateKey];
    return view?.isDefault || false;
  };

  const isCustomDefault = (dashboardId: string | number): boolean => {
    if (!currentDefault) return false;
    return currentDefault.default_type === 'custom' && currentDefault.default_key === String(dashboardId);
  };

  const handleUseSystemView = (viewKey: string): void => {
    const view = systemViews[viewKey];
    if (view && onSelectView) {
      onSelectView({ type: 'system', view, key: viewKey });
    }
    onClose();
  };

  const handleCloneSystemView = async (viewKey: string, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    const view = systemViews[viewKey];
    if (!view) return;

    try {
      const baseName = view.name;
      const existingCustom = customDashboards.filter(d =>
        d.name.startsWith(`${baseName} (Custom`)
      );

      let dashboardName: string;
      if (existingCustom.length === 0) {
        dashboardName = `${baseName} (Custom)`;
      } else {
        const numbers = existingCustom.map(d => {
          const match = d.name.match(/\(Custom (\d+)\)/);
          return match ? Number.parseInt(match[1]) : 1;
        });
        const maxNum = Math.max(...numbers, 1);
        dashboardName = `${baseName} (Custom ${maxNum + 1})`;
      }

      const result = await dashboardAPI.createDashboard({
        name: dashboardName,
        description: view.description,
        context: context,
        layout: { lg: view.widgets },
        is_published: false,
      }, selectedClientId);

      if (result.success && result.data) {
        navigate(`/hub/dashboards/designer/${result.data.id}`);
        onClose();
      } else {
        alert(result.error || 'Failed to clone view');
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : String(err));
    }
  };

  const handleUseCustomView = (dashboard: CustomDashboard): void => {
    if (onSelectView) {
      onSelectView({ type: 'custom', view: dashboard, key: dashboard.id });
    }
    onClose();
  };

  const handleCloneCustomView = async (dashboardId: string | number, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    try {
      const result = await dashboardAPI.getDashboard(String(dashboardId));
      if (result.success && result.data) {
        const dashboard = result.data as CustomDashboard;

        const cloneResult = await dashboardAPI.createDashboard({
          name: `${dashboard.name} (Copy)`,
          description: dashboard.description,
          context: dashboard.context,
          layout: dashboard.layout,
          is_published: false,
        }, selectedClientId);

        if (cloneResult.success && cloneResult.data) {
          navigate(`/hub/dashboards/designer/${cloneResult.data.id}`);
          onClose();
        } else {
          alert(cloneResult.error || 'Failed to clone view');
        }
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : String(err));
    }
  };

  const handleCreateBlank = (): void => {
    navigate('/hub/dashboards/designer');
    onClose();
  };

  const handleEdit = (dashboardId: string | number): void => {
    navigate(`/hub/dashboards/designer/${dashboardId}`);
    onClose();
  };

  const handleDelete = async (dashboardId: string | number, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (!globalThis.confirm('Are you sure you want to delete this view?')) return;

    try {
      const result = await dashboardAPI.deleteDashboard(String(dashboardId));
      if (result.success) {
        loadCustomViews();
      } else {
        alert(result.error || 'Failed to delete view');
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : String(err));
    }
  };

  const handlePublish = async (dashboardId: string | number, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    try {
      const result = await dashboardAPI.publishDashboard(String(dashboardId));
      if (result.success) {
        loadCustomViews();
      } else {
        alert(result.error || 'Failed to share view');
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : String(err));
    }
  };

  const handleUnpublish = async (dashboardId: string | number, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    try {
      const result = await dashboardAPI.unpublishDashboard(String(dashboardId));
      if (result.success) {
        loadCustomViews();
      } else {
        alert(result.error || 'Failed to unpublish view');
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : String(err));
    }
  };

  const handleSetSystemDefault = async (templateKey: string, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    try {
      const result = await dashboardAPI.setDefaultPreference('system', templateKey, context, selectedClientId);
      if (result.success) {
        setCurrentDefault({ default_type: 'system', default_key: templateKey });
        loadCustomViews();
        onDefaultChanged?.();
      } else {
        alert(result.error || 'Failed to set default view');
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : String(err));
    }
  };

  const handleSetCustomDefault = async (dashboardId: string | number, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    try {
      const result = await dashboardAPI.setDefaultPreference('custom', String(dashboardId), context, selectedClientId);
      if (result.success) {
        setCurrentDefault({ default_type: 'custom', default_key: String(dashboardId) });
        loadCustomViews();
        onDefaultChanged?.();
      } else {
        alert(result.error || 'Failed to set default view');
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : String(err));
    }
  };

  const filteredCustomDashboards = customDashboards.filter(dashboard => {
    const matchesSearch = !searchQuery ||
      dashboard.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (dashboard.description || '').toLowerCase().includes(searchQuery.toLowerCase());

    const matchesContext = selectedContext === 'all' ||
      dashboard.context === selectedContext ||
      !dashboard.context;

    return matchesSearch && matchesContext;
  });

  return (
    <div className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Dashboard Views
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Choose a view to get started or create from scratch
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            <FiX className="w-6 h-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-4">
          <button
            onClick={handleCreateBlank}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            <span>Create Blank Dashboard</span>
          </button>

          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search views..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={selectedContext}
            onChange={(e) => setSelectedContext(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Contexts</option>
            <option value="hub">Hub</option>
            <option value="genaiops">GenAIOps</option>
            <option value="agentops">AgentOps</option>
            <option value="dataops">DataOps</option>
            <option value="cloudops">CloudOps</option>
          </select>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {/* System Templates Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FiGrid className="w-5 h-5" />
              System Templates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(systemViews).map(([key, view]) => {
                const isDefault = isSystemDefault(key);
                return (
                  <div
                    key={key}
                    className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-5 border-2 border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 transition-all cursor-pointer"
                    onClick={() => handleUseSystemView(key)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleUseSystemView(key); }} role="button" tabIndex={0}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          {view.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {view.description}
                        </p>
                      </div>
                      {isDefault && (
                        <span className="ml-2 px-2 py-1 text-xs bg-blue-200 dark:bg-blue-700 text-blue-800 dark:text-blue-200 rounded font-medium">
                          Default
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-4">
                      <span className="px-2 py-1 bg-white/50 dark:bg-gray-800/50 rounded">
                        {context}
                      </span>
                      <span>{view.widgets.length} widgets</span>
                    </div>

                    <div className="flex items-center gap-1 text-xs">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUseSystemView(key);
                        }}
                        className="flex items-center gap-1 px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      >
                        <FiCheck className="w-3.5 h-3.5" />
                        <span>Use</span>
                      </button>
                      <span className="text-gray-400">|</span>
                      <button
                        onClick={(e) => handleCloneSystemView(key, e)}
                        className="flex items-center gap-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <FiFilePlus className="w-3.5 h-3.5" />
                        <span>Clone</span>
                      </button>
                      {canSetDefault && !isDefault && (
                        <>
                          <span className="text-gray-400">|</span>
                          <button
                            onClick={(e) => handleSetSystemDefault(key, e)}
                            className="flex items-center gap-1 px-2 py-1.5 border border-yellow-300 dark:border-yellow-600 text-yellow-700 dark:text-yellow-300 rounded hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors"
                            title="Set as Default"
                          >
                            <FiStar className="w-3.5 h-3.5" />
                            <span>Set Default</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Custom Views Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FiGlobe className="w-5 h-5" />
              Custom Views
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                ({filteredCustomDashboards.length})
              </span>
            </h3>

            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : filteredCustomDashboards.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <FiBarChart2 className="w-10 h-10 mb-4 mx-auto text-gray-400" />
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {searchQuery
                    ? 'No views match your search'
                    : 'No custom views available yet'}
                </p>
                {!searchQuery && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Create and share dashboards to make them available as views
                  </p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredCustomDashboards.map((dashboard) => {
                  const userId = user?.id || (user as { user_id?: string | number })?.user_id;
                  const isOwner = userId && dashboard.created_by &&
                    (String(dashboard.created_by) === String(userId));

                  const isPublished = dashboard.is_published;
                  const isDefault = isCustomDefault(dashboard.id);

                  return (
                    <div
                      key={dashboard.id}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-200 dark:border-gray-700"
                    >
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                              {dashboard.name}
                            </h4>
                            {dashboard.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                {dashboard.description}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            {isDefault && (
                              <FiStar className="w-5 h-5 text-yellow-500 fill-yellow-500" title="Default" />
                            )}
                            {isPublished && (
                              <FiGlobe className="w-5 h-5 text-green-500" title="Shared" />
                            )}
                            {!isPublished && isOwner && (
                              <span className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                                Private
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-4">
                          {dashboard.context && (
                            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                              {dashboard.context}
                            </span>
                          )}
                          <span>{dashboard.widget_count || 0} widgets</span>
                          {dashboard.created_by_name && (
                            <span>• by {dashboard.created_by_name}</span>
                          )}
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-1 text-xs">
                            <button
                              onClick={() => handleUseCustomView(dashboard)}
                              className="flex items-center gap-1 px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            >
                              <FiCheck className="w-3.5 h-3.5" />
                              <span>Use</span>
                            </button>
                            <span className="text-gray-400">|</span>
                            <button
                              onClick={(e) => handleCloneCustomView(dashboard.id, e)}
                              className="flex items-center gap-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                              <FiFilePlus className="w-3.5 h-3.5" />
                              <span>Clone</span>
                            </button>
                            <span className="text-gray-400">|</span>
                            <button
                              onClick={() => isOwner ? handleEdit(dashboard.id) : undefined}
                              disabled={!isOwner}
                              className={`flex items-center gap-1 px-2 py-1.5 border rounded transition-colors ${
                                isOwner
                                  ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                  : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                              }`}
                              title={isOwner ? 'Edit' : 'Only owner can edit'}
                            >
                              <FiEdit3 className="w-3.5 h-3.5" />
                              <span>Edit</span>
                            </button>
                            {isPublished && !isDefault && canSetDefault && (
                              <>
                                <span className="text-gray-400">|</span>
                                <button
                                  onClick={(e) => handleSetCustomDefault(dashboard.id, e)}
                                  className="flex items-center gap-1 px-2 py-1.5 border border-yellow-300 dark:border-yellow-600 text-yellow-700 dark:text-yellow-300 rounded hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors"
                                  title="Set as Default"
                                >
                                  <FiStar className="w-3.5 h-3.5" />
                                  <span>Set Default</span>
                                </button>
                              </>
                            )}
                          </div>

                          {isOwner && (
                            <div className="flex items-center gap-1 text-xs">
                              {!isPublished && (
                                <button
                                  onClick={(e) => handlePublish(dashboard.id, e)}
                                  className="flex items-center gap-1 px-2 py-1.5 border border-green-300 dark:border-green-600 text-green-700 dark:text-green-300 rounded hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                                  title="Share to Organization"
                                >
                                  <FiGlobe className="w-3.5 h-3.5" />
                                  <span>Publish</span>
                                </button>
                              )}
                              {isPublished && (
                                <button
                                  onClick={(e) => handleUnpublish(dashboard.id, e)}
                                  className="flex items-center gap-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                  title="Make Private"
                                >
                                  <FiGlobe className="w-3.5 h-3.5" />
                                  <span>Unpublish</span>
                                </button>
                              )}
                              <button
                                onClick={(e) => handleDelete(dashboard.id, e)}
                                className="flex items-center gap-1 px-2 py-1.5 border border-red-300 dark:border-red-600 text-red-700 dark:text-red-300 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                title="Delete"
                              >
                                <FiTrash2 className="w-3.5 h-3.5" />
                                <span>Delete</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewsScreen;
